import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  List,
  ListItem,
  Typography,
  CircularProgress,
  useTheme,
  InputAdornment,
  Theme,
} from '@mui/material';
import { ArrowUpward, PermIdentity } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useDrawRequestMessageHub } from '@hooks/useDrawRequestMessageHub';
import { useDrawRequestMessageApi } from '@hooks/api/useDrawRequestMessageApi';
import type { DrawRequestMessage } from '@models/drawRequests/drawRequestMessage';
import { OriginatorTypes } from '@enums/originatorTypes';
import { RecipientTypes } from '@enums/recipientTypes';
import { MESSAGE_TIMESTAMP_FORMAT_OPTIONS } from '@constants/drawManagerConstants';
import { PrivateRoutes } from '@enums/privateRouteEnums';
import { useRoutePersistence } from '@hooks/useRoutePersistence';
import { clearRouteHistory } from '@services/routePersistenceService';
import { colors } from '@theme/colors';
import { useApp } from '@context/AppContext';

interface DrawRequestMessageProps {
  drawRequestId?: string;
  sourceRecId?: string;
  account?: string;
  onClose?: () => void;
}

const MessagesCard = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  backgroundColor: colors.grey[200],
  padding: theme.spacing(2),
  height: '369px',
  display: 'flex',
  flexDirection: 'column',
}));

const MessagesHeader = styled(Typography)(({ theme }) => ({
  fontFamily: 'Epilogue',
  fontSize: '16px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  mb: 3,
  flexShrink: 0,
}));

const MessagesContainer = styled(Box)(() => ({
  flex: 1,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '8px',
  backgroundColor: colors.grey[100],
}));

const MessageBubble = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: colors.grey[100],
  borderRadius: '18px',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
  maxWidth: '100%',
  wordBreak: 'break-word',
  '& .message-text': {
    ...theme.typography.body2,
    fontSize: '13px',
    fontWeight: 400,
  },
}));

const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  flexShrink: 0,
  backgroundColor: theme.palette.background.paper,
}));

const renderConnectionLoading = (isRoute: boolean) => (
  <MessagesCard
    elevation={0}
    sx={{
      height: isRoute ? 'calc(100vh - 252px)' : '369px',
    }}
  >
    <MessagesHeader>Messages</MessagesHeader>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '300px',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Connecting to messaging service...
      </Typography>
    </Box>
  </MessagesCard>
);

const renderMessagesLoading = (isRoute: boolean) => (
  <MessagesCard
    elevation={0}
    sx={{
      height: isRoute ? 'calc(100vh - 252px)' : '369px',
    }}
  >
    <MessagesHeader>Messages</MessagesHeader>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '300px',
        minHeight: '200px',
      }}
    >
      <CircularProgress />
    </Box>
  </MessagesCard>
);

const renderEmptyMessages = () => (
  <ListItem>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ textAlign: 'center', width: '100%' }}
    >
      No messages yet. Start the conversation!
    </Typography>
  </ListItem>
);

const renderMessageAvatar = (isBorrower: boolean, theme: Theme) => {
  if (isBorrower) return null;

  return (
    <PermIdentity
      sx={{
        fontSize: 20,
        color: theme.palette.grey[600],
        width: 24,
        height: 24,
      }}
    />
  );
};

const renderMessageBubble = (msg: DrawRequestMessage, isBorrower: boolean) => (
  <MessageBubble
    sx={{
      borderRadius: isBorrower ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
    }}
  >
    <Typography variant="body2" component="div" className="message-text">
      {msg.message || ''}
    </Typography>
  </MessageBubble>
);

const renderMessageTimestamp = (
  msg: DrawRequestMessage,
  isBorrower: boolean
) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{
      mt: 0.5,
      alignSelf: isBorrower ? 'flex-end' : 'flex-start',
      fontSize: '11px',
      px: 1,
    }}
  >
    {msg.sentDateTime
      ? new Date(msg.sentDateTime).toLocaleString(
          MESSAGE_TIMESTAMP_FORMAT_OPTIONS.locale,
          MESSAGE_TIMESTAMP_FORMAT_OPTIONS.options
        )
      : 'Pending'}
  </Typography>
);

const renderMessage = (msg: DrawRequestMessage, theme: Theme) => {
  const isBorrower = msg.originatorType === OriginatorTypes.Borrower;

  return (
    <ListItem
      key={msg.recId}
      sx={{
        flexDirection: 'column',
        alignItems: isBorrower ? 'flex-end' : 'flex-start',
        padding: 0,
        marginBottom: 1,
        px: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          width: '100%',
          gap: 1,
          flexDirection: isBorrower ? 'row-reverse' : 'row',
        }}
      >
        {renderMessageAvatar(isBorrower, theme)}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isBorrower ? 'flex-end' : 'flex-start',
            width: '85%',
          }}
        >
          {renderMessageBubble(msg, isBorrower)}
          {renderMessageTimestamp(msg, isBorrower)}
        </Box>
      </Box>
    </ListItem>
  );
};

const renderMessagesList = (messages: DrawRequestMessage[], theme: Theme) => (
  <List>
    {messages.map(msg => renderMessage(msg, theme))}
    {messages.length === 0 && renderEmptyMessages()}
  </List>
);

const renderMessageInput = (
  message: string,
  isConnected: boolean,
  isSending: boolean,
  inputRef: React.RefObject<HTMLInputElement>,
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  onKeyPress: (event: React.KeyboardEvent) => void,
  onSendMessage: () => void,
  onFocus?: () => void
) => (
  <InputContainer>
    <TextField
      ref={inputRef}
      fullWidth
      multiline
      maxRows={4}
      placeholder="Type a message..."
      value={message}
      onChange={onInputChange}
      onKeyPress={onKeyPress}
      disabled={!isConnected || isSending}
      variant="outlined"
      size="small"
      slotProps={{
        htmlInput: {
          maxLength: 2000,
        },
        input: {
          style: { fontSize: '14px' },
          onFocus: onFocus,
          endAdornment: (
            <InputAdornment position="end">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor:
                    isConnected && message.trim() && !isSending
                      ? 'pointer'
                      : 'not-allowed',
                  opacity:
                    isConnected && message.trim() && !isSending ? 1 : 0.5,
                }}
                onClick={
                  isConnected && message.trim() && !isSending
                    ? onSendMessage
                    : undefined
                }
              >
                {isSending ? (
                  <CircularProgress size={20} />
                ) : (
                  <ArrowUpward
                    sx={{
                      color: 'primary.contrastText',
                      fontSize: 20,
                      backgroundColor: 'primary.main',
                      borderRadius: '50%',
                      padding: '2px',
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  />
                )}
              </Box>
            </InputAdornment>
          ),
        },
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '20px',
          backgroundColor: colors.grey[200],
        },
      }}
    />
  </InputContainer>
);

export default function DrawRequestMessage({
  drawRequestId: propDrawRequestId,
  sourceRecId: propSourceRecId,
  account: propAccount,
}: DrawRequestMessageProps) {
  const theme = useTheme();
  const { isTabletScreen } = useApp();
  const isMobile = isTabletScreen;
  const location = useLocation();
  const navigate = useNavigate();
  const { setIntentionalPath } = useRoutePersistence();
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isRoute = location.pathname === PrivateRoutes.DRAW_REQUEST_MESSAGE;

  const handleBackClick = () => {
    const fromPath = location.state?.from;
    const expandedLoan = location.state?.expandedLoan;

    const isValidFromPath =
      fromPath &&
      fromPath !== PrivateRoutes.ROOT &&
      fromPath !== location.pathname;

    if (isValidFromPath) {
      navigate(fromPath, {
        state: {
          expandedLoan: expandedLoan,
        },
      });
    } else {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        clearRouteHistory();
        setIntentionalPath(PrivateRoutes.ROOT);
        navigate(PrivateRoutes.ROOT, {
          replace: true,
          state: { scrollTo: 'your-loans-tile' },
        });
      }
    }
  };

  const drawRequestId =
    propDrawRequestId || searchParams.get('drawRequestId') || '';
  const sourceRecId = propSourceRecId || searchParams.get('sourceRecId') || '';
  const account = propAccount || searchParams.get('account') || '';

  const { messages, isLoading, sendMessage, isSending, isConnected } =
    useDrawRequestMessageHub({
      drawRequestId,
      sourceRecId,
      account,
    });

  const markMessagesAsReadMutation = useDrawRequestMessageApi.useMarkAsRead();
  const lastMarkedMessageCountRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);

  // Track component mount state to prevent API calls after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (drawRequestId && sourceRecId && isMountedRef.current) {
      markMessagesAsReadMutation.mutate(
        { drawRequestId, sourceRecId },
        {
          onSuccess: () => {
            if (isMountedRef.current) {
              lastMarkedMessageCountRef.current = messages.length;
            }
          },
          onError: () => {
            // Silently handle error - network errors during unmount are expected
          },
        }
      );
    }
  }, [drawRequestId, sourceRecId]);

  const unreadMessages = useMemo(() => {
    return messages.filter(
      msg =>
        msg.originatorType === OriginatorTypes.Company &&
        msg.recipientType === RecipientTypes.Borrower &&
        !msg.readDateTime
    );
  }, [messages]);

  useEffect(() => {
    if (!isRoute && !isMobile && drawRequestId && sourceRecId && isMountedRef.current) {
      const hasNewMessages =
        messages.length > lastMarkedMessageCountRef.current;
      const hasUnreadMessages = unreadMessages.length > 0;

      if (hasNewMessages && hasUnreadMessages) {
        markMessagesAsReadMutation.mutate(
          { drawRequestId, sourceRecId },
          {
            onSuccess: () => {
              if (isMountedRef.current) {
                lastMarkedMessageCountRef.current = messages.length;
              }
            },
            onError: () => {
              // Silently handle error - network errors during unmount are expected
            },
          }
        );
      }
    }
  }, [
    messages.length,
    unreadMessages.length,
    isRoute,
    isMobile,
    drawRequestId,
    sourceRecId,
  ]);

  const handleInputFocus = () => {
    if (isMobile && inputRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          inputRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        });
      });
    }
  };

  const handleSendMessage = async () => {
    if (message.trim() && isConnected) {
      await sendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setMessage(value);
  };

  if (!isConnected) {
    return (
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          ...(isRoute && { p: 2, backgroundColor: 'teal.200' }),
        }}
      >
        {isRoute && (
          <Box
            sx={{
              mt: 2,
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Button onClick={handleBackClick} className="back-button">
              {'< Back'}
            </Button>
          </Box>
        )}
        {renderConnectionLoading(isRoute)}
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          ...(isRoute && { p: 2, backgroundColor: 'teal.200' }),
        }}
      >
        {isRoute && (
          <Box
            sx={{
              mt: 2,
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Button onClick={handleBackClick} className="back-button">
              {'< Back'}
            </Button>
          </Box>
        )}
        {renderMessagesLoading(isRoute)}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: '1200px',
        mx: 'auto',
        ...(isRoute && { p: 2, backgroundColor: 'teal.200' }),
      }}
    >
      {isRoute && (
        <Box
          sx={{
            mt: 8,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button onClick={handleBackClick} className="back-button">
            {'< Back'}
          </Button>
        </Box>
      )}
      <MessagesCard
        elevation={0}
        sx={{
          height: isRoute ? 'calc(100vh - 252px)' : '369px',
        }}
      >
        <MessagesHeader>Messages</MessagesHeader>

        <MessagesContainer>
          {renderMessagesList(messages, theme)}
        </MessagesContainer>

        {renderMessageInput(
          message,
          isConnected,
          isSending,
          inputRef,
          handleInputChange,
          handleKeyPress,
          handleSendMessage,
          handleInputFocus
        )}
      </MessagesCard>
    </Box>
  );
}
